import { RouteProp } from "@react-navigation/core"
import { StackNavigationProp } from "@react-navigation/stack"
import { CheckBox } from "react-native-elements"
import { ErrorMessage, Text, TouchButton } from "pusatec-react-native"
import React, { useEffect, useState } from "react"
import { View, Switch, TextInput } from "react-native"
import { useTranslation } from "react-i18next"
import Base from "../../components/layout/Base"
import MainScrollPanel from "../../components/layout/MainScrollPanel"
import Panel from "../../components/layout/Panel"
import Loading from "../../components/Loading"
import { goBack, MainStackParamList } from "../../MainNavigation"
import { HeaderBar } from "../../components/Headerbar"
import { useItemGroupQuery, useUpdateItemGroupMutation } from "../../components/queries.generated"
import { useItemGroupUserGroupsQuery } from "./queries.generated"

interface Props {
	navigation: StackNavigationProp<MainStackParamList, "ItemGroupLimit">
	route: RouteProp<MainStackParamList, "ItemGroupLimit">
}

export const ItemGroupLimitScreen = (props: Props) => {
	const { params } = props.route
	const { t } = useTranslation()
	const [limitEnabled, setLimitEnabled] = useState(false)
	const [limitValue, setLimitValue] = useState("")
	const [groupName, setGroupName] = useState("")
	const [selectedBorrowingGroupIds, setSelectedBorrowingGroupIds] = useState<string[]>([])
	const { data, loading } = useItemGroupQuery({
		variables: {
			itemGroupId: params.itemGroupId
		}
	})
	const { data: userGroupsData } = useItemGroupUserGroupsQuery({
		skip: !data?.itemGroup?.lockersystemId,
		variables: {
			lockersystemId: data?.itemGroup?.lockersystemId ?? ""
		}
	})
	const [updateItemGroup, { error, loading: saving }] = useUpdateItemGroupMutation()
	const itemGroup = data?.itemGroup

	useEffect(() => {
		if (!itemGroup) {
			return
		}

		setGroupName(itemGroup.name)
		setLimitEnabled(itemGroup.maxRentingsPerUserEnabled)
		setLimitValue(itemGroup.maxRentingsPerUser != null ? `${itemGroup.maxRentingsPerUser}` : "")
		setSelectedBorrowingGroupIds(itemGroup.borrowingRightsUserGroups.map(group => group.id))
	}, [itemGroup?.id])

	if (loading && !itemGroup) {
		return <Loading />
	}

	if (!itemGroup) {
		return null
	}

	const parsedLimit = Number.parseInt(limitValue || "", 10)
	const hasLimitValue = !Number.isNaN(parsedLimit)
	const limitValueIsValid = limitEnabled ? hasLimitValue && parsedLimit > 0 : true
	const desiredLimit = hasLimitValue ? parsedLimit : null
	const trimmedGroupName = groupName.trim()
	const groupNameIsValid = trimmedGroupName.length > 0
	const limitHasChanges = itemGroup.maxRentingsPerUserEnabled !== limitEnabled
		|| itemGroup.maxRentingsPerUser !== desiredLimit
	const nameHasChanges = trimmedGroupName !== itemGroup.name
	const borrowingRightsHaveChanges = selectedBorrowingGroupIds.slice().sort().join(",")
		!== itemGroup.borrowingRightsUserGroups.map(group => group.id).slice().sort().join(",")
	const canSave = (limitHasChanges || nameHasChanges || borrowingRightsHaveChanges) && groupNameIsValid && limitValueIsValid

	const availableUserGroups = (userGroupsData?.userGroups ?? [])
		.filter((group): group is NonNullable<typeof group> => !!group)
		.filter(group => group.lockersystemId === itemGroup.lockersystemId)
		.sort((a, b) => a.name.localeCompare(b.name))

	const toggleBorrowingGroup = (groupId: string) => {
		setSelectedBorrowingGroupIds(groupIds => (
			groupIds.includes(groupId)
				? groupIds.filter(id => id !== groupId)
				: [...groupIds, groupId]
		))
	}

	const handleSave = async () => {
		if (!canSave) {
			return
		}

		await updateItemGroup({
			variables: {
				input: {
					itemGroupId: itemGroup.id,
					borrowingRightsUserGroupIds: selectedBorrowingGroupIds,
					maxRentingsPerUserEnabled: limitEnabled,
					maxRentingsPerUser: desiredLimit,
					name: trimmedGroupName
				}
			}
		})

		goBack(props.navigation)
	}

	return (
		<View style={{ flex: 1 }}>
			<MainScrollPanel
				style={{ borderWidth: 0, backgroundColor: undefined }}
				refreshing={false}
				onRefresh={() => { }}
			>
				<Base>
					<HeaderBar text={t("Edit item group")} />
					<Text text={t("Group name")} />
					<TextInput
						placeholder={t("Group name")}
						value={groupName}
						onChangeText={value => setGroupName(value)}
						style={{
							borderColor: "grey",
							borderWidth: 1,
							height: 40,
							marginTop: 8,
							paddingHorizontal: 8
						}}
					/>
					<Text text={t("Max rentings per user")} style={{ marginTop: 16 }} />
					<View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
						<Text text={limitEnabled ? t("Limit enabled") : t("Limit disabled")} />
						<Switch value={limitEnabled} onValueChange={value => setLimitEnabled(value)} />
					</View>
					{!limitEnabled && (
						<Text
							text={`${t("Max rentings per user")}: ${limitValue || t("Not set")}`}
							style={{ marginTop: 8 }}
						/>
					)}
					{limitEnabled && (
						<TextInput
							placeholder={t("Max rentings per user")}
							value={limitValue}
							keyboardType="number-pad"
							onChangeText={value => {
								setLimitValue(value.replace(/[^0-9]/g, ""))
							}}
							style={{
								borderColor: "grey",
								borderWidth: 1,
								height: 40,
								marginTop: 10,
								paddingHorizontal: 8
							}}
						/>
					)}
					<Text text={t("Borrowing rights")} style={{ marginTop: 16 }} />
					<Text
						text={availableUserGroups.length > 0
							? t("If no groups are selected, all lockersystem users can borrow from this item group.")
							: t("No user groups available for this lockersystem yet.")}
						style={{ marginTop: 8 }}
					/>
					{availableUserGroups.map(userGroup => (
						<CheckBox
							key={userGroup.id}
							checked={selectedBorrowingGroupIds.includes(userGroup.id)}
							title={`${userGroup.name} (${userGroup.users.length})`}
							onPress={() => toggleBorrowingGroup(userGroup.id)}
						/>
					))}
				</Base>
			</MainScrollPanel>
			<Panel>
				{error && <ErrorMessage extra={error} />}
				<View style={{ flexDirection: "row", justifyContent: "space-between" }}>
					<TouchButton
						text={t("Cancel")}
						onPress={() => goBack(props.navigation)}
					/>
					<TouchButton
						text={t("Save item group")}
						onPress={handleSave}
						disabled={!canSave || saving}
					/>
				</View>
			</Panel>
		</View>
	)
}
